import {getRankedFullDivisionText} from '../../../../shared/leaderboard_data/LeaderboardEnums.ts'
import {LeaderboardRowData} from '../../../../shared/leaderboard_data/LeaderboardInterfaces.ts'
import {ColonistMembershipProduct} from '../../../../shared/store/ColonistMembershipProduct.ts'
import {UIImagePathController} from '../UIEnums.ts'
import {ViewPopupRankedGameDescription} from '../ui_popup/ViewPopupRankedGameDescription.ts'
import {navigateToPage, navigateToPageOnMobileAppViaLink} from '../ui_utils/ClientUtils.ts'
import {createDiv, createExternalImgIn, createTDIn, setElementInnerHTML, setElementOnClick} from '../ui_utils/DomUtils.ts'
import {clientI18n, clientIntl} from '../ClientI18n.ts'
import {TranslationObject} from '@colonist/i18n'
import {ColonistMembershipType} from '@colonist/types'
import {ContinentCode, CountryCode, RankedDivisionTypes, RankedSubDivisionTypes} from '@colonist/leaderboard-types'
import {getLobbyImagePath} from '../ui_utils/Utils.ts'
import {getCountryOrContinentImageLink} from '@colonist/ui'
import {createTooltipIn} from '../ui_utils/TooltipUtils.ts'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables.ts'
import {ClientUserState} from '../ClientUserState.ts'
import {UIProfileSettingsRegionChangeController} from '../profile/UIProfileSettingsRegionChangeController.ts'
import SocialIconDiscord1 from '@static/images/social_icon_discord_1.svg'
import {ProfilePopupSelectedTab, UIViewProfilePopup} from '../profile/UIViewProfilePopup'

export abstract class UILeaderboardBaseTableRowView {
    readonly rowData: LeaderboardRowData

    readonly tableRow: HTMLTableRowElement
    readonly tdRank: HTMLTableCellElement
    readonly tdDivision: HTMLTableCellElement
    readonly tdUsername: HTMLTableCellElement

    private readonly onLeaderboardUrlChange: (leaderboardUrl: string) => void

    constructor(container: HTMLElement, rowData: LeaderboardRowData, index: number, onLeaderboardUrlChange: (leaderboardUrl: string) => void) {
        this.rowData = rowData
        this.tableRow = (container as HTMLTableSectionElement).insertRow(index)
        const tableRowID = rowData.myData ? 'my_leaderboard_cell' : 'leaderboard_cell'

        this.tdRank = createTDIn(this.tableRow, tableRowID)
        this.tdUsername = createTDIn(this.tableRow, `${tableRowID} truncate-text`)
        this.tdDivision = createTDIn(this.tableRow, tableRowID)

        if(rowData.myData && rowData.division == RankedDivisionTypes.None) UILeaderboardBaseTableRowView.hideCell(this.tdDivision)

        setElementOnClick(this.tableRow, () => {
            UIViewProfilePopup.init(rowData.username, ProfilePopupSelectedTab.Ranked)
        })
        this.onLeaderboardUrlChange = onLeaderboardUrlChange
    }

    protected update(rowData: LeaderboardRowData) {
        const shouldShowHash = typeof rowData.rank == 'number' && !UIUtilsEjsVariables.isUserOnMobileAppOrWeb
        const includeNumberHash = shouldShowHash ? '#' : ''
        const rankText = `${includeNumberHash}${rowData.rank}`
        setElementInnerHTML(this.tdRank, {key: 'strings:colonistCommon.utils.#buffer', options: {value: rankText}})
        const membership = rowData.membershipType
        if(membership !== undefined) {
            const membershipImage = this.getMembershipImg(membership)
            const membershipProduct = ColonistMembershipProduct.getMembershipProduct(membership)
            createTooltipIn(membershipImage, membershipProduct.membershipTitle, 'right')
            this.tdRank.appendChild(membershipImage)
        }

        const divisionImg = UILeaderboardBaseTableRowView.getDivisionImage(rowData.division)
        createTooltipIn(divisionImg, getRankedFullDivisionText(rowData.division, rowData.subdivision), 'right')
        this.tdDivision.replaceChildren(divisionImg)
        const subdivisionImg = UILeaderboardBaseTableRowView.getSubdivisionImage(rowData.subdivision)
        if(subdivisionImg != undefined) this.tdDivision.appendChild(subdivisionImg)

        this.setFlagIcon(rowData)
    }

    private setFlagIcon(rowData: LeaderboardRowData) {
        const neverChangedRegionManually = ClientUserState.userState?.regionUpdated == undefined
        const countryFlagImg = (neverChangedRegionManually && UIUtilsEjsVariables.isUserOnDiscord && rowData.myData) ? UILeaderboardBaseTableRowView.getDiscordIcon() : this.getCountryOrContinentImage(rowData.continentCode, rowData.countryCode, rowData.countryLeaderboardUrl, rowData.myData)

        const countryOrContinentName = UILeaderboardBaseTableRowView.getCountryOrContinentName(rowData)
        const countryOrContinentNameString = clientI18n.t(countryOrContinentName.key, countryOrContinentName.options)
        const currentRegion: TranslationObject = {key: 'strings:leaderboardPage.changeRegion.currentRegion', options: {currentRegion: countryOrContinentNameString}}
        const chooseRegion: TranslationObject = {key: 'strings:leaderboardPage.changeRegion.chooseRegion'}

        if(rowData.myData) {
            countryFlagImg.style.cursor = 'pointer'
            setElementOnClick(countryFlagImg, () => UIProfileSettingsRegionChangeController.showChangeRegionPopup(), false, true)
        }

        const tooltipText = rowData.myData
            ? (neverChangedRegionManually ? chooseRegion : currentRegion)
            : countryOrContinentName

        createTooltipIn(countryFlagImg, tooltipText, 'top')
        this.tdUsername.replaceChildren(countryFlagImg, rowData.username)
        return
    }

    private static getDiscordIcon() {
        return createExternalImgIn(createDiv(), SocialIconDiscord1, {key: 'strings:colonistCommon.utils.emptyString'}, 'flag')
    }

    // eslint-disable-next-line class-methods-use-this
    protected getMembershipImg(membershipType: ColonistMembershipType): HTMLImageElement {
        // Can't use `createImgIn` here since that adds an image, and we need to replace the existing image
        const image: HTMLImageElement = document.createElement('img')
        image.classList.add('membership_icon')
        const membershipProduct = ColonistMembershipProduct.getMembershipProduct(membershipType)
        image.src = getLobbyImagePath(membershipProduct.lobbyImage)
        setElementOnClick(image, () => {
            if(UIUtilsEjsVariables.isUserOnMobileAppOrWeb) navigateToPageOnMobileAppViaLink('/store')
            else navigateToPage('/store', true)
        }, false, true)
        return image
    }

    protected static getCountryOrContinentName(rowData: LeaderboardRowData): TranslationObject {
        if(rowData.countryCode) return {key: 'strings:colonistCommon.utils.#buffer', options: {value: clientIntl.getCountryName(rowData.countryCode)}}
        if(rowData.continentCode) return {key: `strings:leaderboardPage.tabs.continents.${rowData.continentCode}`}
        return {key: 'strings:leaderboardPage.earth'}
    }

    protected static getDivisionImage(division: RankedDivisionTypes): HTMLImageElement {
        // Can't use `createImgIn` here since that adds an image, and we need to replace the existing image
        const lobbyImage = UIImagePathController.getRankedDivisionImage(division)
        const image: HTMLImageElement = document.createElement('img')
        if(division != RankedDivisionTypes.None) image.src = getLobbyImagePath(lobbyImage)
        image.style.cursor = 'pointer'
        setElementOnClick(image, () => {
            new ViewPopupRankedGameDescription()
        }, false, true)
        return image
    }

    protected static getSubdivisionImage(subdivision: RankedSubDivisionTypes): HTMLImageElement | undefined {
        // Can't use `createImgIn` here since that adds an image, and we need to replace the existing image
        const lobbyImage = UIImagePathController.getRankedSubdivisionImage(subdivision)
        if(lobbyImage == undefined) return

        const image: HTMLImageElement = document.createElement('img')
        image.src = getLobbyImagePath(lobbyImage)
        image.style.cursor = 'pointer'
        setElementOnClick(image, () => {
            if(!UIUtilsEjsVariables.isUserOnMobileAppOrWeb) new ViewPopupRankedGameDescription()
        }, false, true)
        return image
    }

    protected getCountryOrContinentImage(continentCode: ContinentCode | undefined, countryCode: CountryCode | undefined, countryLeaderboardUrl: string | undefined, myData: boolean): HTMLImageElement {
        // May 30, 2022. Logan: Can't use `createImgIn` here since that adds an image, and we need to replace the existing image
        const image: HTMLImageElement = document.createElement('img')
        image.src = getCountryOrContinentImageLink(countryCode, continentCode, UIUtilsEjsVariables.isUserOnDiscord)
        image.classList.add('flag')

        if(myData) return image

        if(countryLeaderboardUrl == undefined) return image
        setElementOnClick(image, () => this.onLeaderboardUrlChange(countryLeaderboardUrl), false, true)
        return image
    }

    private static hideCell(cell: HTMLTableCellElement) {
        cell.classList.add('hidden')
    }

    destroy() {
        this.tableRow.remove()
    }
}
